import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import {
  User,
  Mail,
  Phone,
  Building,
  Shield,
  Calendar,
  ArrowLeft,
  Edit,
  Save,
  X,
  Camera,
  Lock,
  Eye,
  EyeOff,
  Upload,
} from "lucide-react";
import { authService } from "../api/authService";
import { userService } from "../api/userService";
import { useNotification } from "../contexts/NotificationContext";

export default function Profile() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const [userData, setUserData] = useState({
    id: "",
    full_name: "",
    email: "",
    phone: "",
    tenant_name: "",
    is_admin: false,
    created_at: "",
    updated_at: "",
    avatar_url: null,
  });

  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
  });

  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      setInitialLoading(true);
      const response = await userService.getProfile();
      const data = response.data;

      // Get tenant info from localStorage
      const user = authService.getCurrentUser();
      const tenant_name =
        user?.tenant_name || localStorage.getItem("tenantName") || "-";
      const is_admin = user?.is_admin || false;

      const profileData = {
        id: data.id || "",
        full_name: data.full_name || "N/A",
        email: data.email || "N/A",
        phone: data.phone || "-",
        tenant_name: tenant_name,
        is_admin: is_admin,
        created_at: data.created_at || new Date().toISOString(),
        updated_at: data.updated_at || new Date().toISOString(),
        avatar_url: data.avatar_url || null,
      };

      setUserData(profileData);
      setFormData({
        full_name: profileData.full_name,
        phone: profileData.phone,
      });

      // Set avatar preview if exists
      if (data.avatar_url) {
        setImagePreview(data.avatar_url);
        localStorage.setItem("avatar_url", data.avatar_url);
      } else {
        localStorage.removeItem("avatar_url");
      }
    } catch (error) {
      console.error("Error loading user data:", error);
      // Fallback to localStorage data if API fails
      const user = authService.getCurrentUser();
      if (user) {
        const data = {
          id: user.id || "",
          full_name: user.full_name || "N/A",
          email: user.email || "N/A",
          phone: user.phone || "-",
          tenant_name:
            user.tenant_name || localStorage.getItem("tenantName") || "-",
          is_admin: user.is_admin || false,
          created_at: user.created_at || new Date().toISOString(),
          updated_at: user.updated_at || new Date().toISOString(),
          avatar_url: null,
        };
        setUserData(data);
        setFormData({
          full_name: data.full_name,
          phone: data.phone,
        });
      }
    } finally {
      setInitialLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError("Ukuran file terlalu besar. Maksimal 5MB");
        return;
      }

      // Validate file type
      if (!file.type.startsWith("image/")) {
        showError("File harus berupa gambar");
        return;
      }

      setProfileImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      full_name: userData.full_name,
      phone: userData.phone,
    });
    // Reset image if changed but not saved
    setImagePreview(userData.avatar_url);
    setProfileImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const payload = {
        full_name: formData.full_name,
        phone: formData.phone,
      };

      // Add avatar if changed
      if (profileImage && imagePreview) {
        payload.avatar_base64 = imagePreview;
        payload.avatar_content_type = profileImage.type;
      }

      const response = await userService.updateProfile(payload);
      const updatedData = response.data;

      // Update local state
      setUserData({
        ...userData,
        full_name: updatedData.full_name,
        phone: updatedData.phone,
        avatar_url: updatedData.avatar_url || userData.avatar_url,
        updated_at: updatedData.updated_at,
      });

      // Update localStorage user data
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          full_name: updatedData.full_name,
          phone: updatedData.phone,
        };
        localStorage.setItem("user_data", JSON.stringify(updatedUser));
      }

      // Update preview with new avatar URL if returned
      if (updatedData.avatar_url) {
        setImagePreview(updatedData.avatar_url);
        localStorage.setItem("avatar_url", updatedData.avatar_url);
        // Dispatch event to update avatar in Header
        window.dispatchEvent(
          new CustomEvent("avatarUpdated", {
            detail: { avatarUrl: updatedData.avatar_url },
          }),
        );
      }
      setProfileImage(null);

      showSuccess("Profile berhasil diperbarui");
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      showError(error.response?.data?.message || "Gagal memperbarui profile");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      // Validate
      if (
        !passwordData.current_password ||
        !passwordData.new_password ||
        !passwordData.confirm_password
      ) {
        showError("Semua field password harus diisi");
        return;
      }

      if (passwordData.new_password !== passwordData.confirm_password) {
        showError("Password baru dan konfirmasi tidak cocok");
        return;
      }

      if (passwordData.new_password === passwordData.current_password) {
        showError("Password baru harus berbeda dari password saat ini");
        return;
      }

      if (passwordData.new_password.length < 6) {
        showError("Password baru minimal 6 karakter");
        return;
      }

      setLoading(true);

      await userService.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });

      showSuccess("Password berhasil diubah");
      setIsChangingPassword(false);
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
      setShowPasswords({
        current: false,
        new: false,
        confirm: false,
      });
    } catch (error) {
      console.error("Error changing password:", error);
      showError(
        error.response?.data?.message ||
          error.message ||
          "Gagal mengubah password",
      );
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }).format(date);
  };

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">
      <div
        className={`fixed inset-y-0 left-0 z-50 transition-all duration-300 pointer-events-none md:pointer-events-auto ${isSidebarCollapsed ? "w-16" : "w-48"}`}>
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() =>
            setIsSidebarCollapsed((prev) => {
              const newState = !prev;
              localStorage.setItem(
                "sidebarCollapsed",
                JSON.stringify(newState),
              );
              return newState;
            })
          }
          activePage=""
        />
      </div>

      <div
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleSidebar={() =>
            setIsSidebarCollapsed((prev) => {
              const newState = !prev;
              localStorage.setItem(
                "sidebarCollapsed",
                JSON.stringify(newState),
              );
              return newState;
            })
          }
        />

        <main className="flex-1 p-6 overflow-x-hidden overflow-y-auto">
          {/* Loading State */}
          {initialLoading ? (
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 p-8 shadow-sm">
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
                  <p className="text-slate-500">Memuat data profile...</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Profile Card */}
              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 overflow-hidden shadow-sm">
                  {/* Header */}
                  <div className="bg-gradient-to-br from-teal-600 to-teal-700 px-6 py-8 relative overflow-hidden">
                    <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
                    <div className="absolute -bottom-12 -left-8 w-48 h-48 bg-teal-400/20 rounded-full blur-3xl" />
                    <div className="relative flex flex-col md:flex-row items-center md:items-start gap-6">
                      {/* Profile Image */}
                      <div className="relative">
                        <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center shadow-lg overflow-hidden ring-4 ring-white/40">
                          {imagePreview ? (
                            <img
                              src={imagePreview}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                              <User className="w-12 h-12 text-teal-600" />
                            </div>
                          )}
                        </div>

                        {/* Upload Button */}
                        {isEditing && (
                          <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-slate-900/40 backdrop-blur-sm cursor-pointer">
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              className="hidden"
                              id="profile-upload"
                            />
                            <label
                              htmlFor="profile-upload"
                              className="cursor-pointer bg-white text-teal-600 rounded-lg p-2 hover:bg-teal-50 transition shadow-sm"
                              title="Upload foto">
                              <Camera className="w-5 h-5" />
                            </label>
                          </div>
                        )}

                        {/* Remove Image Button */}
                        {isEditing && imagePreview && (
                          <button
                            onClick={handleRemoveImage}
                            className="absolute -top-1 -right-1 bg-rose-600 hover:bg-rose-700 text-white rounded-full p-1.5 shadow-md shadow-rose-600/30 transition"
                            title="Hapus foto">
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {/* User Info */}
                      <div className="text-white text-center md:text-left flex-1">
                        <h1 className="text-2xl font-bold tracking-tight">
                          {userData.full_name}
                        </h1>
                        <p className="text-teal-50 text-sm mt-1 flex items-center justify-center md:justify-start gap-2">
                          <Mail className="w-4 h-4" />
                          {userData.email}
                        </p>
                        <div className="flex items-center gap-2 mt-3 justify-center md:justify-start flex-wrap">
                          {userData.is_admin && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs font-semibold ring-1 ring-white/30">
                              <Shield className="w-3 h-3" />
                              System Admin
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-white/15 backdrop-blur-sm rounded-full text-xs font-semibold ring-1 ring-white/30">
                            <Building className="w-3 h-3" />
                            {userData.tenant_name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tabs */}
                  <div className="border-b border-slate-200">
                    <div className="flex">
                      <button
                        onClick={() => setIsChangingPassword(false)}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                          !isChangingPassword
                            ? "text-teal-600 border-teal-600"
                            : "text-slate-500 border-transparent hover:text-slate-700"
                        }`}>
                        <User className="w-4 h-4" />
                        Informasi Profile
                      </button>
                      <button
                        onClick={() => setIsChangingPassword(true)}
                        className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                          isChangingPassword
                            ? "text-teal-600 border-teal-600"
                            : "text-slate-500 border-transparent hover:text-slate-700"
                        }`}>
                        <Lock className="w-4 h-4" />
                        Ganti Password
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    {/* Profile Info Tab */}
                    {!isChangingPassword && (
                      <>
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                              Detail Informasi
                            </h2>
                            <p className="text-sm text-slate-500 mt-1">
                              Kelola informasi pribadi Anda
                            </p>
                          </div>
                          {!isEditing ? (
                            <button
                              onClick={handleEdit}
                              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors shadow-sm shadow-teal-600/20">
                              <Edit className="w-4 h-4" />
                              Edit Profile
                            </button>
                          ) : (
                            <div className="flex gap-3">
                              <button
                                onClick={handleCancel}
                                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
                                <X className="w-4 h-4" />
                                Batal
                              </button>
                              <button
                                onClick={handleSave}
                                disabled={loading}
                                className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 shadow-sm shadow-teal-600/20">
                                <Save className="w-4 h-4" />
                                {loading ? "Menyimpan..." : "Simpan"}
                              </button>
                            </div>
                          )}
                        </div>

                        {isEditing && (
                          <div className="mb-6 p-4 bg-sky-50/60 ring-1 ring-sky-200/70 rounded-xl">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 p-2 bg-sky-600 rounded-lg text-white shadow-sm shadow-sky-600/20">
                                <Upload className="w-4 h-4" />
                              </div>
                              <div className="text-sm text-sky-900">
                                <p className="font-semibold mb-1">
                                  Tips Upload Foto Profile
                                </p>
                                <ul className="space-y-1 text-xs text-sky-800">
                                  <li>• Format file: JPG, PNG, atau GIF</li>
                                  <li>• Ukuran maksimal: 5MB</li>
                                  <li>
                                    • Gunakan foto persegi untuk hasil terbaik
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Full Name */}
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-2">
                              Nama Lengkap
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.full_name}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    full_name: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                                placeholder="Masukkan nama lengkap"
                              />
                            ) : (
                              <div className="px-3 py-2 bg-slate-50 ring-1 ring-slate-200/70 rounded-lg text-slate-900 font-medium">
                                {userData.full_name}
                              </div>
                            )}
                          </div>

                          {/* Email (read-only) */}
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-2">
                              Email
                            </label>
                            <div className="px-3 py-2 bg-slate-50 ring-1 ring-slate-200/70 rounded-lg text-slate-900 font-medium">
                              {userData.email}
                            </div>
                          </div>

                          {/* Phone */}
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-2">
                              No. Handphone
                            </label>
                            {isEditing ? (
                              <input
                                type="text"
                                value={formData.phone}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    phone: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                                placeholder="08xxxxxxxxxx"
                              />
                            ) : (
                              <div className="px-3 py-2 bg-slate-50 ring-1 ring-slate-200/70 rounded-lg text-slate-900 font-medium">
                                {userData.phone || "-"}
                              </div>
                            )}
                          </div>

                          {/* Tenant (read-only) */}
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-2">
                              Toko
                            </label>
                            <div className="px-3 py-2 bg-teal-50/60 ring-1 ring-teal-200/70 rounded-lg text-teal-900 font-medium">
                              {userData.tenant_name}
                            </div>
                          </div>

                          {/* Created At (read-only) */}
                          <div className="lg:col-span-2">
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-2">
                              Tanggal Bergabung
                            </label>
                            <div className="px-3 py-2 bg-slate-50 ring-1 ring-slate-200/70 rounded-lg text-slate-900 font-medium inline-block">
                              {formatDate(userData.created_at)}
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Change Password Tab */}
                    {isChangingPassword && (
                      <>
                        <div className="mb-6">
                          <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                            Ubah Password
                          </h2>
                          <p className="text-sm text-slate-500 mt-1">
                            Pastikan password baru minimal 6 karakter untuk
                            keamanan akun Anda
                          </p>
                        </div>

                        <form
                          className="max-w-2xl space-y-6"
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleChangePassword();
                          }}>
                          {/* Current Password */}
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-2">
                              Password Saat Ini{" "}
                              <span className="text-rose-500">*</span>
                            </label>
                            <div className="relative">
                              <input
                                type={
                                  showPasswords.current ? "text" : "password"
                                }
                                value={passwordData.current_password}
                                onChange={(e) =>
                                  setPasswordData({
                                    ...passwordData,
                                    current_password: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 pr-10 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                                placeholder="Masukkan password saat ini"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setShowPasswords({
                                    ...showPasswords,
                                    current: !showPasswords.current,
                                  })
                                }
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition">
                                {showPasswords.current ? (
                                  <EyeOff className="w-5 h-5" />
                                ) : (
                                  <Eye className="w-5 h-5" />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* New Password */}
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-2">
                              Password Baru{" "}
                              <span className="text-rose-500">*</span>
                            </label>
                            <div className="relative">
                              <input
                                type={showPasswords.new ? "text" : "password"}
                                value={passwordData.new_password}
                                onChange={(e) =>
                                  setPasswordData({
                                    ...passwordData,
                                    new_password: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 pr-10 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                                placeholder="Masukkan password baru (min. 6 karakter)"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setShowPasswords({
                                    ...showPasswords,
                                    new: !showPasswords.new,
                                  })
                                }
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition">
                                {showPasswords.new ? (
                                  <EyeOff className="w-5 h-5" />
                                ) : (
                                  <Eye className="w-5 h-5" />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Confirm Password */}
                          <div>
                            <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-2">
                              Konfirmasi Password Baru{" "}
                              <span className="text-rose-500">*</span>
                            </label>
                            <div className="relative">
                              <input
                                type={
                                  showPasswords.confirm ? "text" : "password"
                                }
                                value={passwordData.confirm_password}
                                onChange={(e) =>
                                  setPasswordData({
                                    ...passwordData,
                                    confirm_password: e.target.value,
                                  })
                                }
                                className="w-full px-3 py-2 pr-10 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                                placeholder="Ulangi password baru"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setShowPasswords({
                                    ...showPasswords,
                                    confirm: !showPasswords.confirm,
                                  })
                                }
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition">
                                {showPasswords.confirm ? (
                                  <EyeOff className="w-5 h-5" />
                                ) : (
                                  <Eye className="w-5 h-5" />
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Security Info */}
                          <div className="p-4 bg-amber-50/60 ring-1 ring-amber-200/70 rounded-xl">
                            <div className="flex items-start gap-3">
                              <div className="flex-shrink-0 p-2 bg-amber-500 rounded-lg text-white shadow-sm shadow-amber-500/20">
                                <Shield className="w-4 h-4" />
                              </div>
                              <div className="text-sm">
                                <p className="font-semibold text-amber-900 mb-1">
                                  Tips Keamanan Password
                                </p>
                                <ul className="space-y-1 text-amber-800 text-xs">
                                  <li>
                                    • Gunakan kombinasi huruf besar, kecil, dan
                                    angka
                                  </li>
                                  <li>
                                    • Jangan gunakan password yang mudah ditebak
                                  </li>
                                  <li>
                                    • Ubah password secara berkala untuk
                                    keamanan
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>

                          {/* Submit Button */}
                          <div className="pt-2">
                            <button
                              type="submit"
                              disabled={loading}
                              className="flex items-center gap-2 px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 font-medium shadow-sm shadow-teal-600/20">
                              <Lock className="w-4 h-4" />
                              {loading
                                ? "Mengubah Password..."
                                : "Ubah Password"}
                            </button>
                          </div>
                        </form>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
